import { confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRightIcon, EditIcon, TrashIcon } from "lucide-react";
import { AppImage } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { VehicleMakeDTO } from "../api/client";

type VehicleMakesTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: VehicleMakeDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (row: VehicleMakeDTO) => void;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function VehicleMakesTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onEdit,
  onDelete,
  onPaginationChange,
}: VehicleMakesTableProps) {
  const nav = useNavigate({ from: "/app/catalog/vehicles" });

  const columns: TanstackReactTable.ColumnDef<VehicleMakeDTO>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "logo",
      header: "Logo",
      cell: ({ row }) => (
        <AppImage
          src={row.original.imageUrl}
          alt={row.original.name}
          className="size-10 rounded border"
          fit="cover"
        />
      ),
      size: 80,
    },
    {
      id: "name",
      header: "ຊື່ຍີ່ຫໍ້",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
      size: 200,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!canManage) return null;
        const id = row.original.id;
        const actions = [
          {
            label: "ຈັດການ Models & Trims",
            icon: <ChevronRightIcon className="size-4" />,
            onClick: () => nav({ to: `/app/catalog/vehicles/${id}` }),
          },
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຍີ່ຫໍ້",
                description: "ຖ້າມີຮຸ່ນອ້າງອີງຢູ່ ຈະລຶບບໍ່ໄດ້. ຕ້ອງການລຶບບໍ?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(id);
            },
          },
        ];
        return <RowActions actions={actions} maxInline={3} />;
      },
      size: 140,
    },
  ];

  return (
    <DataTable<VehicleMakeDTO, unknown>
      noDataMessage="ບໍ່ພົບຂໍ້ມູນຍີ່ຫໍ້ລົດ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
